<template>
  <component
    :is="computedTag"
    ref="button"
    class="ak-btn"
    :class="classes"
    v-bind="attrs"
    @click="clicked"
  >
    <span class="ak-btn__body">
      <template v-if="label">{{ label }}</template>
      <template v-else-if="$slots.default">
        <slot />
      </template>
    </span>
    <span v-if="isLoading" class="ak-btn__loading">
      <ak-loading-spinner :color="loadingColor" size="small" />
    </span>
  </component>
</template>

<script>
import { ref, defineComponent } from 'vue'
import LoadingSpinner from '../loading/LoadingSpinner.vue'
import { buttonMixin, buttonProps } from '../../mixins/buttonMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-button :ghost=false :type="'secondary'">Click Me</ak-button>
   */
  name: 'AkButton',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [LoadingSpinner.name]: LoadingSpinner,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...buttonProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['click'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const button = ref(null)

    const mixin = buttonMixin({
      button,
      props,
      emit,
    })

    return {
      button,
      ...mixin,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
@import '../../scss/_mixins/_custom';
$btn-background: $color-secondary !default;
$btn-color: #fff !default;
$enable-btn--tiny: true;
$enable-btn--small: true;
$enable-btn--large: true;
$enable-btn--full: true;
$enable-btn--ghost: true;
$enable-btn--singular-icon: true;

.ak-btn {
  $self: &;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-family: $base-font-family;
  text-decoration: none;
  border-radius: $btn-radius;
  padding: threequarts($spacing-small) $spacing;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  white-space: nowrap;
  font-weight: bold;
  line-height: 1.2;
  transition: color $transition-time $easing,
    background-color $transition-time $easing, opacity $transition-time $easing,
    border-color $transition-time $easing,
    transform calc($transition-time/2) $easing;
  min-height: 2.625rem;
  outline: 0;
  border: 2px solid transparent;
  overflow: hidden;

  &__body {
    display: flex;
    align-items: center;
    transition: $transition-time all $easing;
  }

  &__loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transition: $transition-time all $easing;
    transform: translateY(-50%) translateX(-50%);
    opacity: 0;
  }

  &.is-loading {
    position: relative;
    cursor: default;
    .ak-btn__body {
      opacity: 0;
    }
    .ak-btn__loading {
      transform: translateY(-50%) translateX(-50%);
      opacity: 1;
    }
  }

  // &.is-active {

  // }

  // &.is-inactive {

  // }

  &[disabled] {
    cursor: not-allowed;
  }

  // Sizes
  &--tiny {
    min-height: 0;
    font-size: $font-size-smaller;
    padding: halve($btn-padding--tiny) double($btn-padding--tiny)
      halve($btn-padding--tiny);
    &.ak-btn--ghost.ak-btn--ghost {
      border-width: 2px;
    }
  }

  &--small {
    min-height: 0;
    padding: threequarts($btn-padding--small) double($btn-padding--small)
      threequarts($btn-padding--small); // fix optical illusion
  }

  &--large {
    min-height: 0;
    padding: threequarts($btn-padding--large) double($btn-padding--large);
  }

  &--full {
    width: 100%;
  }

  // Circular
  &--circle {
    display: inline-flex;
    align-items: center;
    justify-content: space-around;
    width: $input-total-height;
    height: $input-total-height;
    border-radius: 50%;
    padding: 0;
    line-height: 1;
    &.ak-btn--tiny {
      width: 1rem;
      height: 1rem;
    }
    &.ak-btn--small {
      width: 1.75rem;
      height: 1.75rem;
    }
    &.ak-btn--large {
      width: 3.5rem;
      height: 3.5rem;
    }
  }

  &--single-icon {
    padding-left: $spacing-small;
    padding-right: $spacing-small;
    line-height: $base-line-height;
    .ak-icon,
    [class*=' icon-'],
    [class^='icon-'] {
      line-height: $base-line-height * 0.925;
    }
    > * {
      width: ($base-font-size * 1.125);
    }
    &.ak-btn--tiny {
      padding-left: threequarts($btn-padding--small);
      padding-right: threequarts($btn-padding--small);
    }
    &.ak-btn--small {
      padding-top: threequarts($btn-padding--small);
      padding-bottom: threequarts($btn-padding--small);
      padding-left: $btn-padding--small;
      padding-right: $btn-padding--small;
    }
    &.ak-btn--large {
      padding-left: $btn-padding--large;
      padding-right: $btn-padding--large;
    }
  }

  //Colors
  &--primary {
    @include button-variant(#fff, $color-primary, $color-primary);
  }

  &--primary-light {
    @include button-variant(
      $color-grey,
      $color-primary-light,
      $color-primary-light
    );
  }

  &--secondary {
    @include button-variant(#fff, $color-secondary, $color-secondary);
  }

  &--tertiary {
    @include button-variant(#fff, $color-tertiary, $color-tertiary);
  }

  &--quaternary {
    @include button-variant(
      $color-grey-dark,
      $color-quaternary,
      $color-quaternary
    );
  }

  &--grey {
    @include button-variant(#fff, $color-grey, $color-grey);
  }

  &--grey-light {
    @include button-variant(#fff, $color-grey-light, $color-grey-light);
  }

  &--grey-lighter {
    @include button-variant(
      $color-grey-dark,
      $color-grey-lighter,
      $color-grey-lighter
    );
  }

  &--grey-lightest {
    @include button-variant(
      $color-grey-dark,
      $color-grey-lightest,
      $color-grey-lightest
    );
  }

  &--grey-dark {
    @include button-variant(#fff, $color-grey-dark, $color-grey-dark);
  }

  &--grey-darker {
    @include button-variant(#fff, $color-grey-darker, $color-grey-darker);
  }

  &--black {
    @include button-variant(#fff, #000, #000);
  }

  &--white {
    @include button-variant($color-grey-dark, #fff, #fff);
  }

  &--white-primary {
    @include button-variant($color-primary, #fff, #fff);
    &:hover {
      background-color: #fff;
      color: darken($color-primary, 15%);
      border-color: #fff;
    }
  }

  &--white-secondary {
    @include button-variant($color-secondary, #fff, #fff);
    &:hover {
      background-color: #fff;
      color: darken($color-secondary, 15%);
      border-color: #fff;
    }
  }

  &--white-success {
    @include button-variant($color-success, #fff, #fff);
    &:hover {
      background-color: #fff;
      color: darken($color-success, 15%);
      border-color: #fff;
    }
  }

  &--white-danger {
    @include button-variant($color-danger, #fff, #fff);
    &:hover {
      background-color: #fff;
      color: darken($color-danger, 15%);
      border-color: #fff;
    }
  }

  &--white-grey-light {
    @include button-variant($color-grey-light, #fff, #fff);
    &:hover {
      background-color: #fff;
      color: darken($color-grey-light, 15%);
      border-color: #fff;
    }
  }

  &--action {
    @include button-variant(#fff, $color-action, $color-action);
  }

  &--info {
    @include button-variant(#fff, $color-info, $color-info);
  }

  &--success {
    @include button-variant(#fff, $color-success, $color-success);
  }

  &--warning {
    @include button-variant(#fff, $color-warning, $color-warning);
  }

  &--error,
  &--danger {
    @include button-variant(#fff, $color-danger, $color-danger);
  }

  &--semi-transparent {
    color: #fff;
    background-color: rgba(0, 0, 0, 0.15);
    &:hover,
    &:focus,
    &:active {
      background-color: rgba(0, 0, 0, 0.75);
    }
  }

  &--transparent {
    &,
    &:hover,
    &:focus,
    &:active {
      color: currentColor;
      background-color: transparent;
      border-color: transparent;
    }
    &:hover,
    &:focus {
      background-color: rgba(0, 0, 0, 0.1);
    }
    &:active {
      background-color: transparent;
    }
  }

  &--transparent-light {
    &,
    &:hover,
    &:focus,
    &:active {
      color: currentColor;
      background-color: transparent;
      border-color: transparent;
    }
    &:hover,
    &:focus {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  &--transparent-primary {
    &,
    &:hover,
    &:focus,
    &:active {
      color: $color-primary;
      background-color: transparent;
      border-color: transparent;
    }
    &:hover,
    &:focus {
      background-color: rgba(0, 0, 0, 0.1);
    }
    &:active {
      background-color: transparent;
    }
  }

  &--transparent-warning {
    &,
    &:hover,
    &:focus,
    &:active {
      background-color: transparent;
      border-color: transparent;
      color: $color-warning;
    }
    &:hover,
    &:focus {
      background-color: rgba(0, 0, 0, 0.1);
    }
    &:active {
      background-color: transparent;
    }
  }

  &--transparent-danger {
    &,
    &:hover,
    &:focus,
    &:active {
      color: $color-danger;
      background-color: transparent;
      border-color: transparent;
    }
    &:hover,
    &:focus {
      background-color: rgba(0, 0, 0, 0.1);
    }
    &:active {
      background-color: transparent;
    }
  }

  &--transparent-grey-light {
    &,
    &:hover,
    &:focus,
    &:active {
      color: $color-grey-light;
      background-color: transparent;
      border-color: transparent;
    }
    &:hover,
    &:focus {
      background-color: rgba(0, 0, 0, 0.1);
    }
    &:active {
      background-color: transparent;
    }
  }

  // Ghost style buttons
  &--ghost {
    background-color: transparent;
    &.ak-btn--input {
      @include button-variant-ghost($base-border-color, $color-text);
      border-width: 1px;
    }
    &.ak-btn--primary {
      @include button-variant-ghost($color-primary);
    }
    &.ak-btn--secondary {
      @include button-variant-ghost($color-secondary);
    }
    &.ak-btn--tertiary {
      @include button-variant-ghost($color-tertiary);
    }
    &.ak-btn--quaternary {
      @include button-variant-ghost($color-quaternary);
    }
    &.ak-btn--grey {
      @include button-variant-ghost($color-grey);
    }
    &.ak-btn--grey-light {
      @include button-variant-ghost($color-grey-light);
    }
    &.ak-btn--grey-lighter {
      @include button-variant-ghost($color-grey-lighter);
    }
    &.ak-btn--grey-lightest {
      @include button-variant-ghost($color-grey-lightest);
    }
    &.ak-btn--grey-dark {
      @include button-variant-ghost($color-grey-dark);
    }
    &.ak-btn--grey-darker {
      @include button-variant-ghost($color-grey-darker);
    }
    &.ak-btn--black {
      @include button-variant-ghost(#000);
    }
    &.ak-btn--action {
      @include button-variant-ghost($color-action);
    }
    &.ak-btn--info {
      @include button-variant-ghost($color-info);
    }
    &.ak-btn--success {
      @include button-variant-ghost($color-success);
    }
    &.ak-btn--warning {
      @include button-variant-ghost($color-warning);
    }
    &.ak-btn--error,
    &.ak-btn--danger {
      @include button-variant-ghost($color-danger);
    }
  }
}
</style>
